import { ChangeEvent, useCallback, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import type { CreateObjectDraftStepScreenProps } from 'features/kyrgyzstan/create-object/types'

import {
  CreateObjectFieldCard,
  CreateObjectPhotoGrid,
} from 'entities/kyrgyzstan/ui/create-object'

import type { CreateObjectPhoto } from 'shared/api/kyrgyzstan/create-object'
import { KyrgyzstanCreateObjectApi } from 'shared/api/kyrgyzstan/create-object'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib'

import { CreateObjectStepLayout } from '../create-object-step-layout'

import { PhotosStepSkeleton } from './ui/photos-step-skeleton'

import {
  CREATE_OBJECT_PHOTOS_MAX_COUNT,
  CREATE_OBJECT_PHOTOS_MIN_COUNT,
  CREATE_OBJECT_PHOTO_FORMATS_LABEL,
  CREATE_OBJECT_PHOTO_MAX_SIZE_MB,
  CreateObjectPhotoValidationError,
  areCreateObjectPhotosEqual,
  mapCreateObjectPhotoToGridItem,
  validateCreateObjectPhotoFiles,
} from './lib/photos'
import s from './styles.module.scss'

const PHOTOS_INPUT_ID = 'create-object-photos-input'

type PhotosStepOperation =
  | { type: 'idle' }
  | { type: 'loading' }
  | { type: 'uploading'; slotsCount: number }
  | { type: 'submitting' }
  | { type: 'deleting'; photoId: string }

export const PhotosStep = ({
  draftId,
  layoutProps,
  mode = 'create',
  onNext,
}: CreateObjectDraftStepScreenProps) => {
  const { t } = useTranslation()
  const { showApiErrorDialog, showErrorDialog } = useApiErrorDialog()
  const [photos, setPhotos] = useState<CreateObjectPhoto[]>([])
  const [initialPhotos, setInitialPhotos] = useState<CreateObjectPhoto[]>([])
  const [operation, setOperation] = useState<PhotosStepOperation>({
    type: 'idle',
  })
  const hasLocalChanges = !areCreateObjectPhotosEqual(photos, initialPhotos)
  const canContinue =
    photos.length >= CREATE_OBJECT_PHOTOS_MIN_COUNT && operation.type === 'idle'
  const canSubmit = canContinue && (mode !== 'edit' || hasLocalChanges)

  const getValidationErrorDescription = (
    error: CreateObjectPhotoValidationError,
  ) => {
    return t(`kyrgyzstan_block.create_object.steps.photos.errors.${error}`, {
      formats: CREATE_OBJECT_PHOTO_FORMATS_LABEL,
      maxPhotos: CREATE_OBJECT_PHOTOS_MAX_COUNT,
      maxSizeMb: CREATE_OBJECT_PHOTO_MAX_SIZE_MB,
    })
  }

  useEffect(() => {
    const abortController = new AbortController()

    const fetchPhotos = async () => {
      setOperation({ type: 'loading' })

      try {
        const { data } = await KyrgyzstanCreateObjectApi.getPhotosStep(
          draftId,
          abortController.signal,
        )

        setPhotos(data.data.photos)
        setInitialPhotos(data.data.photos)
      } catch (error) {
        if (!abortController.signal.aborted) {
          showApiErrorDialog(error)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setOperation({ type: 'idle' })
        }
      }
    }

    fetchPhotos()

    return () => {
      abortController.abort()
    }
  }, [draftId, showApiErrorDialog])

  const handlePhotosChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? [])

    event.target.value = ''

    if (!selectedFiles.length || operation.type !== 'idle') {
      return
    }

    const validationResult = validateCreateObjectPhotoFiles({
      currentPhotosCount: photos.length,
      files: selectedFiles,
    })

    if (validationResult.error) {
      showErrorDialog(getValidationErrorDescription(validationResult.error))

      return
    }

    setOperation({
      type: 'uploading',
      slotsCount: validationResult.files.length,
    })

    try {
      const { data } = await KyrgyzstanCreateObjectApi.uploadPhotos({
        draftId,
        files: validationResult.files,
        startSortOrder: photos.length,
      })

      setPhotos(data.data)
    } catch (error) {
      showApiErrorDialog(error)
    } finally {
      setOperation({ type: 'idle' })
    }
  }

  const handleRemovePhoto = async (photoId: string) => {
    if (operation.type !== 'idle') {
      return
    }

    setOperation({ type: 'deleting', photoId })

    try {
      await KyrgyzstanCreateObjectApi.deletePhoto(draftId, photoId)

      setPhotos((currentPhotos) =>
        currentPhotos.filter((photo) => photo.id !== photoId),
      )
    } catch (error) {
      showApiErrorDialog(error)
    } finally {
      setOperation({ type: 'idle' })
    }
  }

  const handleContinue = useCallback(async () => {
    if (!canSubmit) {
      return
    }

    setOperation({ type: 'submitting' })
    let shouldGoNext = false

    try {
      await KyrgyzstanCreateObjectApi.completePhotosStep(draftId)

      shouldGoNext = true
    } catch (error) {
      showApiErrorDialog(error)
    } finally {
      setOperation({ type: 'idle' })
    }

    if (shouldGoNext) {
      onNext()
    }
  }, [canSubmit, draftId, onNext, showApiErrorDialog])

  const isAddDisabled =
    operation.type !== 'idle' || photos.length >= CREATE_OBJECT_PHOTOS_MAX_COUNT
  const shouldShowInitialSkeleton =
    operation.type === 'loading' && !photos.length
  const uploadingSlotsCount =
    operation.type === 'uploading' ? operation.slotsCount : 0
  const photoGridItems = photos.map((photo) => {
    const photoGridItem = mapCreateObjectPhotoToGridItem(photo)

    if (operation.type === 'deleting' && operation.photoId === photo.id) {
      return {
        ...photoGridItem,
        status: 'deleting' as const,
      }
    }

    return photoGridItem
  })

  return (
    <CreateObjectStepLayout
      {...layoutProps}
      continueDisabled={!canSubmit}
      continueLoading={operation.type === 'submitting'}
      onContinue={handleContinue}
      title={t('kyrgyzstan_block.create_object.steps.photos.title')}
    >
      <CreateObjectFieldCard
        title={t('kyrgyzstan_block.create_object.steps.photos.helper')}
        description={t(
          'kyrgyzstan_block.create_object.steps.photos.description',
          {
            minPhotos: CREATE_OBJECT_PHOTOS_MIN_COUNT,
            maxPhotos: CREATE_OBJECT_PHOTOS_MAX_COUNT,
            formats: CREATE_OBJECT_PHOTO_FORMATS_LABEL,
          },
        )}
        padding="horizontal"
      >
        <div>
          <div className={s.divider} />

          <span className={clsx(s.hint, 'medium-14')}>
            {t('kyrgyzstan_block.create_object.steps.photos.hint')}
          </span>

          <input
            id={PHOTOS_INPUT_ID}
            className={s.fileInput}
            type="file"
            accept="image/jpeg,image/png"
            multiple
            disabled={isAddDisabled}
            onChange={handlePhotosChange}
          />

          {shouldShowInitialSkeleton ? (
            <PhotosStepSkeleton />
          ) : (
            <CreateObjectPhotoGrid
              photos={photoGridItems}
              minPhotos={CREATE_OBJECT_PHOTOS_MIN_COUNT}
              disabled={operation.type !== 'idle'}
              uploadInputId={PHOTOS_INPUT_ID}
              uploadingSlotsCount={uploadingSlotsCount}
              onRemovePhoto={handleRemovePhoto}
            />
          )}

          <label
            className={clsx(s.uploadButton, 'medium-15', {
              [s.uploadButtonDisabled]: isAddDisabled,
            })}
            htmlFor={isAddDisabled ? undefined : PHOTOS_INPUT_ID}
            aria-disabled={isAddDisabled}
          >
            <AppIcon
              id={AllIcons.PLUS_BOLD}
              width={20}
              height={20}
            />
            {t('kyrgyzstan_block.create_object.steps.photos.upload')}
          </label>
        </div>
      </CreateObjectFieldCard>
    </CreateObjectStepLayout>
  )
}
